/**
 * Multi-Drag Utilities for Consecutive Song Selection
 * 
 * This module provides utility functions for handling multi-select drag-and-drop
 * operations in the playlist songs table. It supports only consecutive selections.
 */

export interface SelectionInfo {
  isConsecutive: boolean;
  startIndex: number;
  endIndex: number;
  count: number;
  selectedIndices: number[];
}

/**
 * Analyzes the current selection to determine if songs are consecutive
 * and can be dragged as a group.
 * 
 * @param selectedSongs - Array of selected song objects with uri and positions
 * @param state - Current playlist state array
 * @returns SelectionInfo object with consecutiveness and range details
 */
export function analyzeSelection(
  selectedSongs: Array<{ uri: string; positions: number[] }>,
  state: any[]
): SelectionInfo {
  const defaultResult: SelectionInfo = {
    isConsecutive: true,
    startIndex: -1,
    endIndex: -1,
    count: 0,
    selectedIndices: [],
  };

  // Empty selection - allow single drag
  if (!selectedSongs || selectedSongs.length === 0) {
    return defaultResult;
  }

  // Single selection - always consecutive
  if (selectedSongs.length === 1) {
    const index = state.findIndex(
      song => song?.rowNumber?.e?.item?.uri === selectedSongs[0].uri
    );
    return {
      isConsecutive: true,
      startIndex: index,
      endIndex: index,
      count: 1,
      selectedIndices: index !== -1 ? [index] : [],
    };
  }

  // Multiple selections - check if consecutive
  const selectedIndices: number[] = [];

  selectedSongs.forEach(selected => {
    const index = state.findIndex(
      song => song?.rowNumber?.e?.item?.uri === selected.uri
    );
    if (index !== -1) {
      selectedIndices.push(index);
    }
  });

  // Sort indices to check consecutiveness
  selectedIndices.sort((a, b) => a - b);

  // Check if all indices are consecutive
  for (let i = 1; i < selectedIndices.length; i++) {
    if (selectedIndices[i] !== selectedIndices[i - 1] + 1) {
      return {
        isConsecutive: false,
        startIndex: -1,
        endIndex: -1,
        count: selectedIndices.length,
        selectedIndices,
      };
    }
  }

  return {
    isConsecutive: true,
    startIndex: selectedIndices[0],
    endIndex: selectedIndices[selectedIndices.length - 1],
    count: selectedIndices.length,
    selectedIndices,
  };
}

/**
 * Moves a range of items in an array to a new position.
 * Used for optimistic UI updates during multi-drag.
 * 
 * @param array - The source array
 * @param startIndex - The starting index of items to move
 * @param count - Number of items to move
 * @param targetIndex - The target position index (the item we're dropping onto)
 * @returns New array with items moved
 */
export function moveRange<T>(
  array: T[],
  startIndex: number,
  count: number,
  targetIndex: number
): T[] {
  const result = [...array];
  const itemsToMove = result.splice(startIndex, count);

  // Calculate insertion point after items are removed
  // When dragging forward: insert AFTER the target (targetIndex + 1), then adjust for removed items
  // When dragging backward: insert AT the target position
  let insertAt: number;
  
  if (targetIndex > startIndex) {
    // Dragging forward: we want items to end up AFTER the drop target
    // After splice removes items, indices shift down by 'count'
    // So: (targetIndex + 1) - count = targetIndex - count + 1
    insertAt = targetIndex - count + 1;
  } else {
    // Dragging backward: insert at the target position
    insertAt = targetIndex;
  }

  result.splice(insertAt, 0, ...itemsToMove);
  return result;
}

/**
 * Calculates the Spotify API insert_before parameter for multi-drag operations.
 * 
 * The Spotify API uses insert_before as the position where items should be inserted.
 * This function calculates the correct value based on the drag direction.
 * 
 * @param startIndex - The starting index of the selection being dragged
 * @param count - Number of items being dragged
 * @param dropTargetIndex - The index where items are being dropped
 * @returns The insert_before value for the Spotify API
 */
export function calculateInsertBefore(
  startIndex: number,
  count: number,
  dropTargetIndex: number
): number {
  // If dropping after the selection, we need to add 1 to account for Spotify's API behavior
  if (dropTargetIndex > startIndex + count - 1) {
    return dropTargetIndex + 1;
  }
  return dropTargetIndex;
}

/**
 * Calculates the adjusted target index for local state updates after items are removed.
 * 
 * @param startIndex - The starting index of the selection being dragged
 * @param count - Number of items being dragged  
 * @param dropTargetIndex - The index where items are being dropped
 * @returns The adjusted target index for local array operations
 */
export function calculateAdjustedTarget(
  startIndex: number,
  count: number,
  dropTargetIndex: number
): number {
  return dropTargetIndex > startIndex
    ? dropTargetIndex - count
    : dropTargetIndex;
}
